import { groupBy } from '@range.io/functional'
import { tickAsDataUri } from '@range.io/functional/src/categories/default-categories.js'

import {
    Canvas as PdfCanvas,
    Document,
    Font,
    Image as PdfImage,
    Link,
    Path,
    StyleSheet,
    Svg,
    Text,
    View,
} from '@react-pdf/renderer'
import { format } from 'date-fns'
import React, { useRef } from 'react'
import { humanizeByteCount } from '../../helpers.js'

import InterBold from './fonts/Inter-Bold.ttf'
import InterLight from './fonts/Inter-Light.ttf'
import InterMedium from './fonts/Inter-Medium.ttf'
import InterRegular from './fonts/Inter-Regular.ttf'
import InterSemiBold from './fonts/Inter-SemiBold.ttf'
import { Footer, Header, Page, PageContent, UserAvatar } from './shared'

Font.register({
    family: 'Inter',
    fonts: [
        { fontWeight: 300, src: InterLight },
        { fontWeight: 400, src: InterRegular },
        { fontWeight: 500, src: InterMedium },
        { fontWeight: 600, src: InterSemiBold },
        { fontWeight: 700, src: InterBold },
    ],
})
Font.registerEmojiSource({
    format: 'png',
    url: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/',
})

const styles = StyleSheet.create({
    summary: {
        height: 'auto',
        backgroundColor: '#FAFAFA',
        border: '1pt solid #D9D9D9',
        borderRadius: '5pt',
        padding: '14pt',
        gap: '14pt',
        margin: '18pt 0pt 18pt 0pt',
    },
    summaryHeader: {
        fontWeight: '700',
        fontSize: 10,
        color: '#333333',
        textTransform: 'uppercase',
    },
    summaryRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: '12pt',
        pb: '15pt',
        alignItems: 'start',
        width: '100%',
    },
    summaryItemName: {
        fontSize: 9,
        fontWeight: '700',
        textTransform: 'uppercase',
        color: '#333333',
        display: 'block',
    },
    summaryItemValue: {
        fontSize: 12,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontWeight: '500',
        color: '#333333',
    },

    sheetHeader: {
        display: 'flex',
        flexDirection: 'row',
        gap: 6,
        alignItems: 'center',
        width: '100%',
        paddingBottom: 24,
    },
    sheetHeaderLocation: {
        fontSize: 11,
        minWidth: 'auto',
        maxWidth: '413pt',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontWeight: '500',
    },
    sheetHeaderLabel: {
        color: '#333333',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: 9,
    },

    itemDetailLabel: { fontSize: 10, fontWeight: '600', color: '#333333', flex: '0 0 auto', minWidth: 72 },
    itemDetailText: {
        whiteSpace: 'wrap',
        fontWeight: '500',
        fontSize: 10,
        color: '#333333',
        maxWidth: 176,
    },
    itemLabel: { color: '#333333', fontSize: '10pt', fontWeight: '600' },
    itemDescription: { color: '#333333', fontSize: 10, paddingBottom: '3pt' },

    snapshotLabel: {
        whiteSpace: 'wrap',
        fontWeight: '500',
        fontSize: 10,
        color: '#333333',
    },

    assigneesContainer: {
        width: '100%',
        display: 'flex',
        gap: 6,
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    assigneeWrapper: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    assigneeName: {
        fontSize: 12,
        fontWeight: '500',
        minWidth: 'auto',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    assigneeLabel: {
        fontSize: 10,
        fontWeight: '400',
        minWidth: 'auto',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },

    itemTagWrapper: {
        borderRadius: '50%',
        border: '1pt solid #5D81FF',
        height: '16pt',
        padding: '0pt 5pt 0pt 5pt',
        whiteSpace: 'nowrap',
        display: 'flex',
        ai: 'center',
        justifyContent: 'center',
    },
    itemTagName: {
        color: '#5D81FF',
        fontSize: 9,
        fontWeight: '600',
    },

    commentLabel: {
        color: '#333333',
        fontSize: '9pt',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
})

const CalendarIcon = ({ fill }) => (
    <Svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
        <Path
            d="M2.12421 8.92855H7.87593C8.7992 8.92855 9.28578 8.44529 9.28578 7.53293V2.4712C9.28578 1.55884 8.7992 1.07141 7.87593 1.07141H2.12421C1.2051 1.07141 0.714355 1.55884 0.714355 2.4712V7.53293C0.714355 8.44529 1.2051 8.92855 2.12421 8.92855ZM2.15332 8.00369C1.82061 8.00369 1.64178 7.83705 1.64178 7.48294V3.70018C1.64178 3.35023 1.82061 3.18359 2.15332 3.18359H7.84266C8.17537 3.18359 8.35836 3.35023 8.35836 3.70018V7.48294C8.35836 7.83705 8.17537 8.00369 7.84266 8.00369H2.15332ZM4.20781 4.58754H4.45318C4.6029 4.58754 4.6528 4.54172 4.6528 4.39174V4.14595C4.6528 3.99597 4.6029 3.95014 4.45318 3.95014H4.20781C4.05809 3.95014 4.00818 3.99597 4.00818 4.14595V4.39174C4.00818 4.54172 4.05809 4.58754 4.20781 4.58754ZM5.55112 4.58754H5.79649C5.94621 4.58754 5.99612 4.54172 5.99612 4.39174V4.14595C5.99612 3.99597 5.94621 3.95014 5.79649 3.95014H5.55112C5.4014 3.95014 5.35149 3.99597 5.35149 4.14595V4.39174C5.35149 4.54172 5.4014 4.58754 5.55112 4.58754ZM6.89443 4.58754H7.13981C7.28953 4.58754 7.34359 4.54172 7.34359 4.39174V4.14595C7.34359 3.99597 7.28953 3.95014 7.13981 3.95014H6.89443C6.74472 3.95014 6.69481 3.99597 6.69481 4.14595V4.39174C6.69481 4.54172 6.74472 4.58754 6.89443 4.58754ZM2.86449 5.91234H3.10986C3.25958 5.91234 3.30949 5.86652 3.30949 5.71654V5.47074C3.30949 5.32077 3.25958 5.27494 3.10986 5.27494H2.86449C2.71477 5.27494 2.66071 5.32077 2.66071 5.47074V5.71654C2.66071 5.86652 2.71477 5.91234 2.86449 5.91234ZM4.20781 5.91234H4.45318C4.6029 5.91234 4.6528 5.86652 4.6528 5.71654V5.47074C4.6528 5.32077 4.6029 5.27494 4.45318 5.27494H4.20781C4.05809 5.27494 4.00818 5.32077 4.00818 5.47074V5.71654C4.00818 5.86652 4.05809 5.91234 4.20781 5.91234ZM5.55112 5.91234H5.79649C5.94621 5.91234 5.99612 5.86652 5.99612 5.71654V5.47074C5.99612 5.32077 5.94621 5.27494 5.79649 5.27494H5.55112C5.4014 5.27494 5.35149 5.32077 5.35149 5.47074V5.71654C5.35149 5.86652 5.4014 5.91234 5.55112 5.91234ZM6.89443 5.91234H7.13981C7.28953 5.91234 7.34359 5.86652 7.34359 5.71654V5.47074C7.34359 5.32077 7.28953 5.27494 7.13981 5.27494H6.89443C6.74472 5.27494 6.69481 5.32077 6.69481 5.47074V5.71654C6.69481 5.86652 6.74472 5.91234 6.89443 5.91234ZM2.86449 7.23714H3.10986C3.25958 7.23714 3.30949 7.19132 3.30949 7.04134V6.79554C3.30949 6.64557 3.25958 6.59974 3.10986 6.59974H2.86449C2.71477 6.59974 2.66071 6.64557 2.66071 6.79554V7.04134C2.66071 7.19132 2.71477 7.23714 2.86449 7.23714ZM4.20781 7.23714H4.45318C4.6029 7.23714 4.6528 7.19132 4.6528 7.04134V6.79554C4.6528 6.64557 4.6029 6.59974 4.45318 6.59974H4.20781C4.05809 6.59974 4.00818 6.64557 4.00818 6.79554V7.04134C4.00818 7.19132 4.05809 7.23714 4.20781 7.23714ZM5.55112 7.23714H5.79649C5.94621 7.23714 5.99612 7.19132 5.99612 7.04134V6.79554C5.99612 6.64557 5.94621 6.59974 5.79649 6.59974H5.55112C5.4014 6.59974 5.35149 6.64557 5.35149 6.79554V7.04134C5.35149 7.19132 5.4014 7.23714 5.55112 7.23714Z"
            fill={fill}
        />
    </Svg>
)

const LayersIcon = () => (
    <Svg viewBox="0 0 16 16" width="12" height="12">
        <Path
            d="M16 8.01283C15.9998 7.9563 15.985 7.90078 15.9569 7.8517C15.9289 7.80261 15.8886 7.76165 15.84 7.73281L13.8397 6.50603L15.84 5.28592C15.8882 5.25519 15.9278 5.21281 15.9553 5.1627C15.9828 5.1126 15.9972 5.05637 15.9972 4.99922C15.9972 4.94208 15.9828 4.88585 15.9553 4.83574C15.9278 4.78564 15.8882 4.74326 15.84 4.71253L8.17222 0.0454414C8.1193 0.0156504 8.0596 0 7.99887 0C7.93814 0 7.87843 0.0156504 7.82551 0.0454414L0.157761 4.71253C0.109576 4.74326 0.0699133 4.78564 0.0424368 4.83574C0.0149603 4.88585 0.000556639 4.94208 0.000556639 4.99922C0.000556639 5.05637 0.0149603 5.1126 0.0424368 5.1627C0.0699133 5.21281 0.109576 5.25519 0.157761 5.28592L2.15804 6.50603L0.157761 7.73281C0.109518 7.7627 0.0697064 7.80442 0.0420984 7.85401C0.0144904 7.9036 0 7.95941 0 8.01617C0 8.07292 0.0144904 8.12873 0.0420984 8.17832C0.0697064 8.22791 0.109518 8.26963 0.157761 8.29952L2.15804 9.5063L0.157761 10.7131C0.109576 10.7438 0.0699133 10.7862 0.0424368 10.8363C0.0149603 10.8864 0.000556639 10.9426 0.000556639 10.9998C0.000556639 11.0569 0.0149603 11.1131 0.0424368 11.1632C0.0699133 11.2134 0.109576 11.2557 0.157761 11.2865L7.82551 15.9536C7.87822 15.984 7.93801 16 7.99887 16C8.05973 16 8.11952 15.984 8.17222 15.9536L15.84 11.2865C15.8882 11.2557 15.9278 11.2134 15.9553 11.1632C15.9828 11.1131 15.9972 11.0569 15.9972 10.9998C15.9972 10.9426 15.9828 10.8864 15.9553 10.8363C15.9278 10.7862 15.8882 10.7438 15.84 10.7131L13.8397 9.5063L15.84 8.29952C15.8891 8.26961 15.9297 8.22748 15.9577 8.17724C15.9858 8.127 16.0003 8.07036 16 8.01283V8.01283ZM7.99887 0.738838L15.0265 5.01256L7.99887 9.28628L0.971209 5.01256L7.99887 0.738838ZM15.0265 11.0131L7.99887 15.2868L0.971209 11.0131L2.74479 9.93301L7.79217 13.0066C7.84488 13.037 7.90467 13.0531 7.96553 13.0531C8.02639 13.0531 8.08618 13.037 8.13889 13.0066L13.1996 9.92634L15.0265 11.0131ZM7.99887 12.3066L0.971209 8.01283L2.79813 6.90606L7.82551 9.96634C7.87822 9.99677 7.93801 10.0128 7.99887 10.0128C8.05973 10.0128 8.11952 9.99677 8.17222 9.96634L13.2129 6.8994L15.0265 8.01283L7.99887 12.3066Z"
            fill="#333333"
        />
    </Svg>
)

const Divider = () => (
    <PdfCanvas
        style={{ width: '100%', height: 'auto', minHeight: '1px', margin: '20pt 0' }}
        paint={(painter, availableWidth) =>
            painter.moveTo(0, 0).lineWidth(1).strokeColor('#D9D9D9').lineTo(availableWidth, 0).stroke()
        }
    />
)

const DividerSummary = () => (
    <PdfCanvas
        style={{ width: '100%', height: 'auto', minHeight: '1px', margin: '4pt 0' }}
        paint={(painter, availableWidth) =>
            painter.moveTo(0, 0).lineWidth(1).strokeColor('#D9D9D9').lineTo(availableWidth, 0).stroke()
        }
    />
)

const SummaryHeaderItem = ({ name, children, asWarning, style }) => (
    <View style={{ display: 'flex', flexDirection: 'column', gap: '9pt', ...style }}>
        <Text style={[styles.summaryItemName, { color: asWarning ? '#F13D15' : '#333333' }]}>{name}</Text>
        <View style={[styles.summaryItemValue, { color: asWarning ? '#F13D15' : '#333333' }]}>{children}</View>
    </View>
)

const SheetHeader = ({ locationLabel, tasksCount }) => {
    const sheetSummaryLabel = `(${tasksCount} task${tasksCount > 1 ? 's' : ''})`
    return (
        <View style={styles.sheetHeader}>
            <Svg viewBox="0 0 16 16" width="12" height="12">
                <Path
                    d="M16 8.01283C15.9998 7.9563 15.985 7.90078 15.9569 7.8517C15.9289 7.80261 15.8886 7.76165 15.84 7.73281L13.8397 6.50603L15.84 5.28592C15.8882 5.25519 15.9278 5.21281 15.9553 5.1627C15.9828 5.1126 15.9972 5.05637 15.9972 4.99922C15.9972 4.94208 15.9828 4.88585 15.9553 4.83574C15.9278 4.78564 15.8882 4.74326 15.84 4.71253L8.17222 0.0454414C8.1193 0.0156504 8.0596 0 7.99887 0C7.93814 0 7.87843 0.0156504 7.82551 0.0454414L0.157761 4.71253C0.109576 4.74326 0.0699133 4.78564 0.0424368 4.83574C0.0149603 4.88585 0.000556639 4.94208 0.000556639 4.99922C0.000556639 5.05637 0.0149603 5.1126 0.0424368 5.1627C0.0699133 5.21281 0.109576 5.25519 0.157761 5.28592L2.15804 6.50603L0.157761 7.73281C0.109518 7.7627 0.0697064 7.80442 0.0420984 7.85401C0.0144904 7.9036 0 7.95941 0 8.01617C0 8.07292 0.0144904 8.12873 0.0420984 8.17832C0.0697064 8.22791 0.109518 8.26963 0.157761 8.29952L2.15804 9.5063L0.157761 10.7131C0.109576 10.7438 0.0699133 10.7862 0.0424368 10.8363C0.0149603 10.8864 0.000556639 10.9426 0.000556639 10.9998C0.000556639 11.0569 0.0149603 11.1131 0.0424368 11.1632C0.0699133 11.2134 0.109576 11.2557 0.157761 11.2865L7.82551 15.9536C7.87822 15.984 7.93801 16 7.99887 16C8.05973 16 8.11952 15.984 8.17222 15.9536L15.84 11.2865C15.8882 11.2557 15.9278 11.2134 15.9553 11.1632C15.9828 11.1131 15.9972 11.0569 15.9972 10.9998C15.9972 10.9426 15.9828 10.8864 15.9553 10.8363C15.9278 10.7862 15.8882 10.7438 15.84 10.7131L13.8397 9.5063L15.84 8.29952C15.8891 8.26961 15.9297 8.22748 15.9577 8.17724C15.9858 8.127 16.0003 8.07036 16 8.01283V8.01283ZM7.99887 0.738838L15.0265 5.01256L7.99887 9.28628L0.971209 5.01256L7.99887 0.738838ZM15.0265 11.0131L7.99887 15.2868L0.971209 11.0131L2.74479 9.93301L7.79217 13.0066C7.84488 13.037 7.90467 13.0531 7.96553 13.0531C8.02639 13.0531 8.08618 13.037 8.13889 13.0066L13.1996 9.92634L15.0265 11.0131ZM7.99887 12.3066L0.971209 8.01283L2.79813 6.90606L7.82551 9.96634C7.87822 9.99677 7.93801 10.0128 7.99887 10.0128C8.05973 10.0128 8.11952 9.99677 8.17222 9.96634L13.2129 6.8994L15.0265 8.01283L7.99887 12.3066Z"
                    fill="#333333"
                />
            </Svg>
            <View style={{ display: 'flex', flexDirection: 'row', gap: '3pt', alignItems: 'center' }}>
                <Text style={styles.sheetHeaderLocation}>{locationLabel}</Text>
                <Text style={styles.sheetHeaderLabel}>{sheetSummaryLabel}</Text>
            </View>
        </View>
    )
}

const LocationSnapshot = ({ locationSnapshotUrl }) => (
    <View
        style={{
            borderRadius: '5pt',
            border: '1pt solid #DDDDDD',
        }}
    >
        <Link src={locationSnapshotUrl}>
            <PdfImage
                style={{
                    'object-fit': 'contain',
                    width: 'auto',
                    height: 'auto',
                    borderRadius: '4pt',
                    maxHeight: '175pt',
                }}
                src={locationSnapshotUrl}
            />
        </Link>
    </View>
)

const ItemDetailLabel = ({ children }) => <Text style={[styles.itemDetailLabel]}>{children}</Text>
const ItemDetailContent = ({ children }) => (
    <View
        style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'left',
            gap: 4,
            flex: '1 0 auto',
            fontSize: 10,
            fontWeight: 500,
            color: '#333333',
        }}
    >
        {children}
    </View>
)

const ItemDetail = ({ children }) => {
    return (
        <View
            wrap={false}
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                fontSize: 9,
            }}
        >
            {children}
        </View>
    )
}

const TagsList = ({ tags, reportItemId }) => {
    return (
        <View
            style={{
                height: 'auto',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: '4pt',
                alignItems: 'center',
                bottomPadding: '6pt',
            }}
        >
            {tags.map((tagName, idx) => (
                <View wrap={false} style={styles.itemTagWrapper} key={`tag_name_${reportItemId}_${idx}`}>
                    <Text style={styles.itemTagName} key={`tagName_report_${idx}`}>
                        {tagName}
                    </Text>
                </View>
            ))}
        </View>
    )
}

const StatusPill = ({ backgroundColor, children, css }) => (
    <View
        wrap={false}
        style={{
            backgroundColor,
            borderRadius: '50%',
            height: '18pt',
            padding: '2pt 6pt 3pt 6pt',
            whiteSpace: 'nowrap',
            display: 'flex',
            ai: 'center',
            justifyContent: 'center',
            width: 'auto',
            ...css,
        }}
    >
        {children}
    </View>
)

const FilePreviewThumbnail = ({ name, fileType, fileSize }) => {
    const text = () => {
        if (fileType === 'pdf') return '.pdf'
        if (fileType === 'xlsx') return '.xlxs'
        if (fileType === 'docx') return '.docx'
        return ''
    }

    const humanized = humanizeByteCount(fileSize)
    return (
        <View
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                textAlign: 'center',
                backgroundColor: '#EEEEEE',
                justifyContent: 'center',
                border: '1px solid #DDDDDD',
                borderRadius: 5,
                width: '20%',
                height: '100pt',
            }}
        >
            <View
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    textAlign: 'center',
                    padding: 10,
                    justifyContent: 'center',
                }}
            >
                <View
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                        justifyContent: 'center',
                    }}
                >
                    <Text
                        style={{
                            maxWidth: '80%',
                            fontSize: 6,
                            fontWeight: 700,
                            color: '#333333',
                            display: 'flex',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxLines: 1,
                            padding: '0 9pt 0 0',
                        }}
                    >
                        {name.split('')}
                    </Text>
                    <Text style={{ fontSize: 6, fontWeight: 700, color: '#333333' }}>{text()}</Text>
                </View>
                {humanized ? <Text style={{ fontSize: 5, fontWeight: 500, color: '#333333' }}>{humanized}</Text> : null}
            </View>
        </View>
    )
}

const UploadsList = ({ uploads }) => {
    const getUploadThumbnail = (upload, key) => {
        const { thumbnailUrl, imageUrl, fileType, name, fileSize } = upload
        const isImage = fileType === 'image' || fileType === 'jpeg' || fileType === 'png'

        if (isImage && thumbnailUrl)
            return (
                <View
                    wrap={false}
                    style={{
                        'aspect-ratio': '4/3',
                        height: 'auto',
                        maxHeight: '180pt',
                    }}
                >
                    <Link src={imageUrl}>
                        <PdfImage
                            key={`upload_report_${key}`}
                            style={{
                                borderRadius: 5,
                                border: '1pt solid #DDDDDD',
                                objectFit: 'cover',
                                height: '180pt',
                            }}
                            src={thumbnailUrl}
                        />
                    </Link>
                </View>
            )
        return <FilePreviewThumbnail fileType={fileType} fileSize={fileSize} name={name} />
    }

    return uploads.map(getUploadThumbnail)
}

const CommentsList = ({ comments }) => {
    const formatDate = date => format(Date.parse(date), 'd MMM, yyyy @ h:mm' + 'aaa')

    return comments.map(comment => (
        <View key={comment.id} style={{ flexDirection: 'column', gap: '6pt' }} wrap={false}>
            <View
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '4pt',
                }}
            >
                <UserAvatar name={comment.authorName} url={comment.authorAvatarUrl} size={12} />
                <Text style={styles.commentLabel}>{comment.authorName}</Text>
                <Text style={styles.commentLabel}>•</Text>
                <Text style={styles.commentLabel}>{formatDate(comment.createdAt)}</Text>
            </View>
            <Text style={styles.itemDescription}>{comment.text}</Text>
        </View>
    ))
}

// Convert Base64 SVG to PNG using a Canvas
const convertSvgBase64ToPng = async svgBase64 => {
    return new Promise(resolve => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const img = new Image()

        img.onload = () => {
            canvas.width = img.width
            canvas.height = img.height
            ctx.drawImage(img, 0, 0)
            resolve(canvas.toDataURL('image/png'))
        }

        img.src = svgBase64
    })
}

/*
 * Given a category icon src in base64 return a dataURI with that icon colored to white
 */
const getColoredCategoryIcon = async categoryIconSrc => {
    return new Promise(resolve => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const image = new Image()

        image.onload = () => {
            canvas.width = image.width
            canvas.height = image.height
            ctx.drawImage(image, 0, 0, canvas.width, canvas.height)

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
            const data = imageData.data

            for (let i = 0; i < data.length; i += 4) {
                if (data[i + 3] > 0) {
                    data[i] = 255
                    data[i + 1] = 255
                    data[i + 2] = 255
                }
            }

            ctx.putImageData(imageData, 0, 0)
            resolve(canvas.toDataURL('image/png', 1.0))
        }

        image.src = categoryIconSrc
    })
}

const ReportItem = ({
    index,
    description,
    locationLabel,
    locationSnapshotUrl,
    name,
    tagNames,
    avatarUrl,
    dueDate,
    isOverdue,
    statusNameId,
    completedDate,
    uploadsData,
    assignee,
    statusNamesWithCount,
    date,
    userName,
    userAvatarUrl,
    completedBy,
    comments,
    projectIdentifier,
    identifier,
    geometryData,
    category,
}) => {
    const statusNameWithCount = statusNamesWithCount.filter(entity => entity.status.id === statusNameId)[0]
    const createdDateFormatted = date ? format(Date.parse(date), 'd MMM, yyyy') : '-'
    const dueDateFormatted = dueDate ? format(Date.parse(dueDate), 'd MMM') : '-'
    const completedDateFormatted = completedDate
        ? format(Date.parse(completedDate), 'd MMM, yyyy @ h:mm' + 'aaa')
        : null

    const getCategoryIcon = async () => {
        const result = await getColoredCategoryIcon(category.isDefault ? tickAsDataUri : category.icon)
        return result
    }

    return (
        <View
            style={{
                display: 'flex',
                width: '100%',
                flexDirection: 'column',
            }}
        >
            <View
                style={{
                    display: 'flex',
                    width: '100%',
                    flexDirection: 'column',
                }}
            >
                <Text style={{ fontSize: 14, fontWeight: '600', color: '#333333', marginBottom: '6pt' }}>
                    {index}. {name || 'Untitled task'}
                </Text>
                {description ? (
                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                        }}
                    >
                        <View
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'left',
                                gap: '4pt',
                                width: '100%',
                                marginBottom: '8pt',
                            }}
                        >
                            <View style={{ flex: '1', flexWrap: 'wrap' }}>
                                <Text style={styles.itemDescription}>{description}</Text>
                            </View>
                        </View>
                    </View>
                ) : null}
                <View
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 16,
                    }}
                >
                    <View style={{ flex: '1 1 45%', gap: '10pt' }}>
                        <View
                            style={{
                                marginBottom: '4pt',
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                width: '100%',
                                gap: 4,
                            }}
                        >
                            <div
                                style={{
                                    width: 18,
                                    height: 18,
                                    backgroundColor: statusNameWithCount?.status?.color,
                                    borderRadius: '999',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <PdfImage
                                    src={() => getCategoryIcon()}
                                    style={{
                                        width: 16,
                                        height: 16,
                                        fill: '#fff',
                                    }}
                                />
                            </div>
                            <StatusPill
                                key={`status_report_${index}_${statusNameWithCount.status.name}`}
                                backgroundColor={statusNameWithCount.status.color}
                                css={{ width: 'auto' }}
                            >
                                <Text style={{ fontSize: 10, fontWeight: '500', color: '#fff' }}>
                                    {statusNameWithCount.status.name}
                                </Text>
                            </StatusPill>
                            {completedDate ? (
                                <Text style={{ fontSize: 10, fontWeight: '500' }}>{completedDateFormatted}</Text>
                            ) : null}
                        </View>
                        {tagNames.length ? <TagsList tags={tagNames} reportItemId={index} /> : null}

                        <DividerSummary />
                        {!category.isDefault && (
                            <ItemDetail>
                                <ItemDetailLabel>Category:</ItemDetailLabel>
                                <ItemDetailContent>
                                    <PdfImage
                                        src={() => convertSvgBase64ToPng(category.icon)}
                                        style={{ width: 14, height: 14, marginLeft: -4 }}
                                    />
                                    <Text>{category.name}</Text>
                                </ItemDetailContent>
                            </ItemDetail>
                        )}
                        {identifier ? (
                            <ItemDetail>
                                <ItemDetailLabel>ID:</ItemDetailLabel>
                                <ItemDetailContent>
                                    <Text>
                                        {projectIdentifier}-{identifier}
                                    </Text>
                                </ItemDetailContent>
                            </ItemDetail>
                        ) : null}

                        <ItemDetail>
                            <ItemDetailLabel>Created On:</ItemDetailLabel>
                            <ItemDetailContent>
                                <Text>{createdDateFormatted}</Text>
                            </ItemDetailContent>
                        </ItemDetail>

                        <ItemDetail>
                            <ItemDetailLabel>Created By:</ItemDetailLabel>
                            <ItemDetailContent>
                                <UserAvatar name={userName} url={userAvatarUrl} size={12} />
                                <Text style={styles.itemDetailText}>{userName}</Text>
                            </ItemDetailContent>
                        </ItemDetail>

                        <ItemDetail>
                            <ItemDetailLabel>Assignee:</ItemDetailLabel>
                            <ItemDetailContent>
                                {!assignee?.firstName && !assignee?.lastName ? (
                                    <Text>-</Text>
                                ) : (
                                    <>
                                        <UserAvatar
                                            name={assignee?.firstName || assignee?.lastName}
                                            url={avatarUrl}
                                            size={12}
                                        />
                                        <Text style={styles.itemDetailText}>{`${assignee?.firstName || ''} ${
                                            assignee?.lastName || ''
                                        }`}</Text>
                                    </>
                                )}
                            </ItemDetailContent>
                        </ItemDetail>

                        {completedBy.userFullName ? (
                            <ItemDetail>
                                <ItemDetailLabel>Completed By:</ItemDetailLabel>
                                <ItemDetailContent>
                                    <UserAvatar name={completedBy.userFullName} url={completedBy.avatarUrl} size={12} />
                                    <Text style={styles.itemDetailText}>{completedBy.userFullName}</Text>
                                </ItemDetailContent>
                            </ItemDetail>
                        ) : null}

                        <ItemDetail>
                            <ItemDetailLabel>Due Date:</ItemDetailLabel>
                            <ItemDetailContent>
                                <CalendarIcon fill={isOverdue ? '#F13D15' : '#333333'} />
                                <Text
                                    style={[
                                        styles.itemDetailText,
                                        {
                                            color: isOverdue ? '#F13D15' : '#333333',
                                        },
                                    ]}
                                >
                                    {isOverdue ? `${dueDateFormatted} (Overdue)` : dueDateFormatted}
                                </Text>
                            </ItemDetailContent>
                        </ItemDetail>

                        {geometryData?.lat && geometryData?.lng ? (
                            <ItemDetail>
                                <ItemDetailLabel>Lat / Lng:</ItemDetailLabel>
                                <ItemDetailContent>
                                    <Text
                                        style={styles.itemDetailText}
                                    >{`${geometryData.lat} / ${geometryData?.lng}`}</Text>
                                </ItemDetailContent>
                            </ItemDetail>
                        ) : null}

                        {geometryData?.elevationMetres || geometryData?.elevationFeet ? (
                            <ItemDetail>
                                <ItemDetailLabel>Elevation:</ItemDetailLabel>
                                <ItemDetailContent>
                                    <Text style={styles.itemDetailText}>
                                        {geometryData?.elevationMetres}m / {geometryData?.elevationFeet}ft
                                    </Text>
                                </ItemDetailContent>
                            </ItemDetail>
                        ) : null}
                    </View>
                    <View style={{ flex: '1 1 45%', position: 'relative' }} wrap={false}>
                        {locationSnapshotUrl ? <LocationSnapshot locationSnapshotUrl={locationSnapshotUrl} /> : null}
                        <View
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '6pt',
                                marginTop: '8pt',
                            }}
                        >
                            <LayersIcon />
                            <Text style={styles.snapshotLabel}>{locationLabel}</Text>
                        </View>
                    </View>
                </View>
            </View>

            {comments?.length ? (
                <View
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        paddingTop: 6,
                    }}
                >
                    <Divider />
                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'left',
                            gap: '4pt',
                            width: '100%',
                        }}
                    >
                        <View style={{ flex: '0 0 15%' }}>
                            <Text style={styles.itemLabel}>Comments:</Text>
                        </View>
                        <View
                            style={{
                                flex: '1 1 83%',
                                flexWrap: 'wrap',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'left',
                                gap: '16pt',
                            }}
                        >
                            <CommentsList comments={comments} />
                        </View>
                    </View>
                </View>
            ) : null}
            {uploadsData?.length ? (
                <View
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        paddingTop: 6,
                    }}
                >
                    <DividerSummary />
                    <Text style={{ ...styles.itemLabel, margin: '10pt 0pt' }}>Media:</Text>
                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'start',
                            flexWrap: 'wrap',
                            width: '100%',
                            gap: 12,
                        }}
                    >
                        <UploadsList uploads={uploadsData} />
                    </View>
                </View>
            ) : null}
        </View>
    )
}

const AssigneesList = ({ assignees, tasksCount }) => {
    return (
        <View style={styles.assigneesContainer}>
            {assignees.map((assignee, key) => (
                <View style={styles.assigneeWrapper} key={`assignee_report_${key}`}>
                    <UserAvatar name={assignee.firstName} url={assignee.avatarUrl} />
                    <Text style={styles.assigneeName}>{`${assignee.firstName} ${assignee.lastName}`}</Text>
                    <Text style={styles.assigneeLabel}>
                        ({`${assignee.tasksCount} task${tasksCount > 1 ? 's' : ''}`})
                    </Text>
                </View>
            ))}
        </View>
    )
}

const StatusCategoriesList = ({ statusNamesWithCount }) => {
    return (
        <View style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '9pt', alignItems: 'center' }}>
            {statusNamesWithCount.map((category, idx) => (
                <StatusPill key={`statuses_report_${idx}`} backgroundColor={category.status.color}>
                    <Text style={{ fontSize: 10, fontWeight: '500', color: '#fff' }} key={`tagName_report_${idx}`}>
                        {category.status.name} ({category.tasksCount})
                    </Text>
                </StatusPill>
            ))}
        </View>
    )
}

const TasksReportPdfDoc = ({
    organizationName,
    logo,
    projectName,
    dateRangeString,
    tasksData,
    tasksCount,
    overdueCount,
    assignees,
    statusNamesWithCount,
    tags,
}) => {
    const groupedByCanvas = groupBy(u => u?.canvas, tasksData)
    const sheetsCount = Object.keys(groupedByCanvas).length
    const itemsCount = useRef(0)

    return (
        <Document>
            <Page>
                <PageContent>
                    <Header logo={logo} organizationName={organizationName} projectName={projectName} />
                    <View style={styles.summary}>
                        <Text style={styles.summaryHeader}>Report Summary</Text>
                        <View
                            style={[
                                styles.summaryRow,
                                {
                                    justifyContent: 'space-between',
                                },
                            ]}
                        >
                            <SummaryHeaderItem
                                name="Date range"
                                style={{
                                    minWidth: '40%',
                                    maxWidth: '50%',
                                    marginRight: '10pt',
                                }}
                            >
                                <Text>{dateRangeString}</Text>
                            </SummaryHeaderItem>
                            <View
                                style={[
                                    styles.summaryRow,
                                    {
                                        justifyContent: 'space-around',
                                    },
                                ]}
                            >
                                <SummaryHeaderItem name="Total tasks">
                                    <Text>{tasksCount}</Text>
                                </SummaryHeaderItem>
                                <SummaryHeaderItem name="Overdue tasks" asWarning={overdueCount > 0}>
                                    <Text>{overdueCount}</Text>
                                </SummaryHeaderItem>
                                <SummaryHeaderItem name="Total sheets">
                                    <Text>{sheetsCount}</Text>
                                </SummaryHeaderItem>
                            </View>
                        </View>
                        <DividerSummary />
                        <View style={styles.summaryRow}>
                            <SummaryHeaderItem
                                name="Task assignees"
                                style={{
                                    flex: '0 0 50%',
                                }}
                            >
                                <AssigneesList assignees={assignees} tasksCount={tasksCount} />
                            </SummaryHeaderItem>
                            <SummaryHeaderItem
                                name="Tasks include the following tags"
                                style={{
                                    flex: '0 0 50%',
                                }}
                            >
                                <TagsList tags={tags} />
                            </SummaryHeaderItem>
                        </View>
                        <DividerSummary />
                        <View style={styles.summaryRow}>
                            <SummaryHeaderItem name="Tasks by category">
                                <StatusCategoriesList statusNamesWithCount={statusNamesWithCount} />
                            </SummaryHeaderItem>
                        </View>
                    </View>

                    {Object.keys(groupedByCanvas).map((canvasId, idx) => (
                        <View key={`report_group_${idx}`}>
                            <SheetHeader
                                tasksCount={groupedByCanvas[canvasId].length}
                                locationLabel={groupedByCanvas[canvasId][0].locationLabel}
                            />
                            {groupedByCanvas[canvasId].map((task, idx) => (
                                <View key={`report_item_${idx}`} wrap>
                                    <ReportItem
                                        {...task}
                                        index={++itemsCount.current}
                                        statusNamesWithCount={statusNamesWithCount}
                                    />
                                    <Divider />
                                </View>
                            ))}
                        </View>
                    ))}
                </PageContent>
                <Footer organizationName={organizationName} />
            </Page>
        </Document>
    )
}

export default TasksReportPdfDoc
